// Typed editors for org-config values that were previously only reachable as raw
// JSON. Each one owns a single value shape and hands the parent a parsed value —
// so the page never has to parse text, and an admin never has to know the schema.
//
// Nullability is deliberate throughout: rv2_org_config distinguishes "not set"
// (null) from an explicit value, and clearing a control returns null rather than
// an empty string or empty array.
//
// Each control accepts an optional `anchor`: it stamps `data-field` on the root
// element so the sidebar's field search can scroll to and flash a specific field.
// MUI's own TextField already forwards `data-field` to its root, so plain
// TextFields in the page pass that attribute directly instead.
import { FC, useEffect, useRef, useState, KeyboardEvent } from "react";

import { Add, Delete, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

/** Section heading used to group related fields inside a tab. */
export const FieldGroup: FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.secondary" }}>
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

/**
 * A list of short string tokens as chips. Enter or comma commits the draft; a
 * chip's X removes it. Returns null when emptied, matching the nullable columns.
 */
export const ChipListField: FC<{
  label: string;
  value: string[] | null | undefined;
  onChange: (v: string[] | null) => void;
  helperText?: string;
  placeholder?: string;
  /** Flags a chip visually when it fails. Never blocks entry — existing data
   *  can be malformed, and refusing it would make it uneditable. */
  validateItem?: (item: string) => boolean;
  size?: "small" | "medium";
  anchor?: string;
}> = ({
  label,
  value,
  onChange,
  helperText,
  placeholder,
  validateItem,
  size,
  anchor,
}) => {
  const [draft, setDraft] = useState("");
  const list = value ?? [];

  const commit = (raw: string) => {
    const additions = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s !== "" && !list.includes(s));
    if (additions.length > 0) onChange([...list, ...additions]);
    setDraft("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && list.length > 0) {
      onChange(list.slice(0, -1).length ? list.slice(0, -1) : null);
    }
  };

  const remove = (item: string) => {
    const next = list.filter((i) => i !== item);
    onChange(next.length ? next : null);
  };

  return (
    <Box data-field={anchor}>
      <TextField
        fullWidth
        size={size}
        label={label}
        value={draft}
        placeholder={placeholder ?? "Type a value and press Enter"}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        helperText={
          helperText ?? "Enter or comma to add; click a chip's X to remove"
        }
      />
      {list.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", gap: 1, mt: 1 }}
        >
          {list.map((item) => {
            const bad = validateItem ? !validateItem(item) : false;
            return (
              <Chip
                key={item}
                label={item}
                size="small"
                color={bad ? "error" : "default"}
                variant={bad ? "outlined" : "filled"}
                onDelete={() => remove(item)}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

/**
 * Nullable boolean. A never-set flag stays null until the admin actually toggles
 * it, so we don't silently rewrite every unset flag to `false` on first save.
 */
export const BoolField: FC<{
  label: string;
  value: boolean | null | undefined;
  onChange: (v: boolean) => void;
  helperText?: string;
  anchor?: string;
}> = ({ label, value, onChange, helperText, anchor }) => (
  <Box data-field={anchor}>
    <FormControlLabel
      control={
        <Switch
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{label}</Typography>
          {value == null && (
            <Chip label="not set" size="small" variant="outlined" />
          )}
        </Stack>
      }
    />
    {helperText && (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", ml: 6 }}
      >
        {helperText}
      </Typography>
    )}
  </Box>
);

/**
 * Editable rows for an array of two-field objects — `{label, value}` option
 * lists and `{name, value}` priorities. `numericValue` switches the second input
 * to a number so priorities round-trip as integers, not strings.
 */
export function PairListField<T extends Record<string, string | number>>({
  label,
  value,
  onChange,
  keyProp,
  valueProp,
  keyLabel,
  valueLabel,
  numericValue = false,
  helperText,
  anchor,
}: {
  label: string;
  value: T[] | null | undefined;
  onChange: (v: T[] | null) => void;
  keyProp: keyof T & string;
  valueProp: keyof T & string;
  keyLabel: string;
  valueLabel: string;
  numericValue?: boolean;
  helperText?: string;
  anchor?: string;
}) {
  const rows = value ?? [];

  const update = (i: number, prop: string, raw: string) => {
    const next = rows.map((row, idx) =>
      idx === i
        ? {
            ...row,
            [prop]:
              prop === valueProp && numericValue
                ? raw === ""
                  ? 0
                  : Number(raw)
                : raw,
          }
        : row,
    ) as T[];
    onChange(next);
  };

  const addRow = () =>
    onChange([
      ...rows,
      { [keyProp]: "", [valueProp]: numericValue ? 0 : "" } as unknown as T,
    ]);

  const removeRow = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next.length ? next : null);
  };

  return (
    <Box data-field={anchor}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label={keyLabel}
              value={String(row[keyProp] ?? "")}
              onChange={(e) => update(i, keyProp, e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label={valueLabel}
              type={numericValue ? "number" : "text"}
              value={String(row[valueProp] ?? "")}
              onChange={(e) => update(i, valueProp, e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton
              aria-label={`Remove ${label} row ${i + 1}`}
              onClick={() => removeRow(i)}
              size="small"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button startIcon={<Add />} size="small" onClick={addRow} sx={{ mt: 1 }}>
        Add row
      </Button>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {helperText ??
          (rows.length === 0 ? "None set" : `${rows.length} row(s)`)}
      </Typography>
    </Box>
  );
}

/** Hex colour with a swatch and the native picker; clearing returns null. */
export const ColourField: FC<{
  label: string;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  helperText?: string;
  anchor?: string;
}> = ({ label, value, onChange, helperText, anchor }) => {
  const valid = /^#[0-9a-f]{6}$/i.test(value ?? "");
  return (
    <TextField
      data-field={anchor}
      fullWidth
      label={label}
      value={value ?? ""}
      placeholder="#1A1B2B"
      onChange={(e) => onChange(e.target.value.trim() || null)}
      helperText={
        helperText ??
        "Six-digit hex, e.g. #1A1B2B. Leave empty to use the default."
      }
      error={!!value && !valid}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {/* Native picker needs a valid value; fall back to a neutral base. */}
            <input
              type="color"
              aria-label={`${label} colour picker`}
              value={valid ? (value as string) : "#000000"}
              onChange={(e) => onChange(e.target.value.toUpperCase())}
              style={{
                width: 28,
                height: 28,
                border: "none",
                background: "none",
                padding: 0,
                cursor: "pointer",
              }}
            />
          </InputAdornment>
        ),
      }}
    />
  );
};

/** `{title, url}` pair (the citizen "non-technical issues" link). */
export const TitleUrlField: FC<{
  label: string;
  value: { title: string; url: string } | null | undefined;
  onChange: (v: { title: string; url: string } | null) => void;
  helperText?: string;
  anchor?: string;
}> = ({ label, value, onChange, helperText, anchor }) => {
  const set = (patch: Partial<{ title: string; url: string }>) => {
    const next = { title: value?.title ?? "", url: value?.url ?? "", ...patch };
    onChange(next.title === "" && next.url === "" ? null : next);
  };
  return (
    <Box data-field={anchor}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <TextField
          size="small"
          label="Link text"
          value={value?.title ?? ""}
          onChange={(e) => set({ title: e.target.value })}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="URL"
          value={value?.url ?? ""}
          onChange={(e) => set({ url: e.target.value })}
          sx={{ flex: 2 }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {helperText ?? "Both empty clears the link."}
      </Typography>
    </Box>
  );
};

/** One response note: body + footer copy, for a single status and language. */
export const ResponseNoteField: FC<{
  label: string;
  value: {
    body_content?: string | null;
    footer_content?: string | null;
  } | null;
  onChange: (
    v: { body_content: string | null; footer_content: string | null } | null,
  ) => void;
  anchor?: string;
}> = ({ label, value, onChange, anchor }) => {
  const set = (patch: {
    body_content?: string | null;
    footer_content?: string | null;
  }) => {
    const next = {
      body_content: value?.body_content ?? null,
      footer_content: value?.footer_content ?? null,
      ...patch,
    };
    onChange(
      !next.body_content && !next.footer_content
        ? null
        : {
            body_content: next.body_content || null,
            footer_content: next.footer_content || null,
          },
    );
  };
  return (
    <Box data-field={anchor}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Body"
          value={value?.body_content ?? ""}
          onChange={(e) => set({ body_content: e.target.value })}
        />
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Footer"
          value={value?.footer_content ?? ""}
          onChange={(e) => set({ footer_content: e.target.value })}
        />
      </Stack>
    </Box>
  );
};

// --- notify / templates -----------------------------------------------------
//
// Both columns are arrays of small objects with a fully known shape, so they get
// real controls rather than a JSON textarea. Every writer spreads the original
// object before overwriting the fields it owns: the shapes come from untagged
// Rust enums (TemplateInner) and may carry keys this UI doesn't model yet, and
// silently dropping those on an unrelated edit would be data loss.

/** `email@host.tld`, loosely. Only used to tint a chip, never to block input. */
const looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

type NotifyItemLike = { key: string; emails: string[] };

/**
 * `{key, emails}[]` — one recipient list per report class (or "all"). The key is
 * a survey class slug, so it stays free text; the emails are chips.
 */
export function NotifyItemsField<T extends NotifyItemLike>({
  value,
  onChange,
  keyLabel = "Key",
  keyHelper,
  addLabel = "Add recipient list",
  emptyText = "No recipient lists.",
}: {
  value: T[] | null | undefined;
  onChange: (v: T[]) => void;
  keyLabel?: string;
  keyHelper?: string;
  addLabel?: string;
  emptyText?: string;
}) {
  const rows = value ?? [];

  const setRow = (i: number, patch: Partial<NotifyItemLike>) =>
    onChange(
      rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row)) as T[],
    );

  return (
    <Stack spacing={1.5}>
      {rows.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {emptyText}
        </Typography>
      )}
      {rows.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Stack spacing={1} sx={{ flex: 1 }}>
            <TextField
              size="small"
              label={keyLabel}
              value={row.key ?? ""}
              onChange={(e) => setRow(i, { key: e.target.value })}
              error={!row.key}
              helperText={
                !row.key
                  ? "Empty key — this list will not match anything"
                  : keyHelper
              }
            />
            <ChipListField
              size="small"
              label="Emails"
              value={row.emails ?? []}
              onChange={(v) => setRow(i, { emails: v ?? [] })}
              validateItem={looksLikeEmail}
              placeholder="name@agency.ca"
              helperText="Enter or comma to add. Chips outlined in red don't look like addresses."
            />
          </Stack>
          <IconButton
            aria-label={`Remove ${row.key || "unnamed"} recipient list`}
            size="small"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i) as T[])}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          startIcon={<Add />}
          size="small"
          onClick={() =>
            onChange([...rows, { key: "", emails: [] } as unknown as T])
          }
        >
          {addLabel}
        </Button>
      </Box>
    </Stack>
  );
}

type StatusGroupLike = { status: string; items: NotifyItemLike[] };

/**
 * `{status, items}[]` — recipient lists that only apply once a report reaches a
 * given status. Status is free text (the data uses approved / rejected /
 * escalated) with those offered as suggestions.
 */
export function StatusNotifyGroupsField<T extends StatusGroupLike>({
  value,
  onChange,
  statusOptions = ["approved", "rejected", "escalated"],
}: {
  value: T[] | null | undefined;
  onChange: (v: T[] | null) => void;
  statusOptions?: string[];
}) {
  const groups = value ?? [];
  const setGroup = (i: number, patch: Partial<StatusGroupLike>) =>
    onChange(
      groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g)) as T[],
    );

  return (
    <Stack spacing={1.5}>
      {groups.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No per-status recipient lists.
        </Typography>
      )}
      {groups.map((group, i) => (
        <Accordion key={i} disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flex: 1 }}
            >
              <Typography variant="body2">
                {group.status || "(no status)"}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={`${group.items?.length ?? 0} list(s)`}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                select
                size="small"
                label="Status"
                value={group.status ?? ""}
                onChange={(e) => setGroup(i, { status: e.target.value })}
                sx={{ maxWidth: 260 }}
                error={!group.status}
                helperText={!group.status ? "Required" : undefined}
              >
                {/* Keep an unrecognised existing status selectable rather than
                    silently rewriting it. */}
                {Array.from(
                  new Set([...statusOptions, group.status].filter(Boolean)),
                ).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <NotifyItemsField
                value={group.items ?? []}
                onChange={(items) => setGroup(i, { items })}
              />
              <Box>
                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    const next = groups.filter((_, idx) => idx !== i);
                    onChange(next.length ? (next as T[]) : null);
                  }}
                >
                  Remove {group.status || "group"}
                </Button>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      <Box>
        <Button
          startIcon={<Add />}
          size="small"
          onClick={() =>
            onChange([
              ...groups,
              { status: "", items: [] } as unknown as T,
            ] as T[])
          }
        >
          Add status group
        </Button>
      </Box>
    </Stack>
  );
}

type TemplateLike = { name: string; plain_text: string };

/**
 * Named plain-text reply templates. Bodies run to several paragraphs, so each
 * one collapses to its name and the first line until opened.
 */
export function TemplateListField<T extends TemplateLike>({
  value,
  onChange,
}: {
  value: T[] | null | undefined;
  onChange: (v: T[]) => void;
}) {
  const templates = value ?? [];
  const setTemplate = (i: number, patch: Partial<TemplateLike>) =>
    onChange(
      templates.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) as T[],
    );

  return (
    <Stack spacing={1.5}>
      {templates.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No templates. Reviewers will have nothing to pick from.
        </Typography>
      )}
      {templates.map((t, i) => (
        <Accordion key={i} disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {t.name || "(unnamed template)"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block" }}
              >
                {(t.plain_text ?? "").split("\n")[0] || "empty"}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={t.name ?? ""}
                onChange={(e) => setTemplate(i, { name: e.target.value })}
                error={!t.name}
                helperText={
                  !t.name
                    ? "Required — this is what reviewers pick from"
                    : undefined
                }
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Body (plain text)"
                value={t.plain_text ?? ""}
                onChange={(e) => setTemplate(i, { plain_text: e.target.value })}
                helperText="Sent as-is. **DOUBLE-ASTERISK** markers are conventional placeholders a reviewer fills in before sending."
              />
              <Box>
                <Button
                  size="small"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() =>
                    onChange(templates.filter((_, idx) => idx !== i) as T[])
                  }
                >
                  Remove template
                </Button>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
      <Box>
        <Button
          startIcon={<Add />}
          size="small"
          onClick={() =>
            onChange([
              ...templates,
              { name: "", plain_text: "" } as unknown as T,
            ])
          }
        >
          Add template
        </Button>
      </Box>
    </Stack>
  );
}

// --- export configuration ---------------------------------------------------

/**
 * Free text with suggestions. Providers and export models are effectively an
 * enum in practice but not in the schema, so a new one must stay typeable.
 */
export const SuggestField: FC<{
  label: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  options: string[];
  helperText?: string;
  required?: boolean;
}> = ({ label, value, onChange, options, helperText, required }) => (
  <Autocomplete
    freeSolo
    options={options}
    value={value ?? ""}
    onChange={(_, v) => onChange(v ?? "")}
    onInputChange={(_, v) => onChange(v)}
    renderInput={(params) => (
      <TextField
        {...params}
        size="small"
        label={label}
        error={required && !value}
        helperText={
          required && !value
            ? "Required"
            : (helperText ??
              "Known values suggested; anything else is accepted.")
        }
      />
    )}
  />
);

/**
 * A `Record<string, string | string[]>` keyed by survey class (plus "all"), as
 * editable rows. Rows are held locally so renaming a key doesn't reorder the
 * list or steal focus mid-keystroke; the parent only ever sees the rebuilt
 * object. Re-syncs if the parent value changes underneath us (e.g. Reset).
 */
export function KeyedMapField<V extends string | string[]>({
  label,
  value,
  onChange,
  multiValue,
  keyLabel = "Survey class",
  valueLabel,
  helperText,
  anchor,
}: {
  label: string;
  value: Record<string, V> | null | undefined;
  onChange: (v: Record<string, V> | null) => void;
  multiValue: boolean;
  keyLabel?: string;
  valueLabel: string;
  helperText?: string;
  anchor?: string;
}) {
  const toRows = (v: Record<string, V> | null | undefined) =>
    Object.entries(v ?? {}).map(([k, val], i) => ({ id: i, k, v: val }));
  const [rows, setRows] = useState(() => toRows(value));
  const [seq, setSeq] = useState(rows.length);

  const emitted = useRef<string>(JSON.stringify(value ?? null));
  const incoming = JSON.stringify(value ?? null);
  useEffect(() => {
    if (incoming !== emitted.current) {
      setRows(toRows(value));
      emitted.current = incoming;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  const push = (next: { id: number; k: string; v: V }[]) => {
    setRows(next);
    // Later duplicates of a key would silently win; flagged in the UI below.
    const obj: Record<string, V> = {};
    for (const r of next) if (r.k !== "") obj[r.k] = r.v;
    const out = Object.keys(obj).length ? obj : null;
    emitted.current = JSON.stringify(out);
    onChange(out);
  };

  const duplicateKeys = new Set(
    rows
      .map((r) => r.k)
      .filter((k, i, arr) => k !== "" && arr.indexOf(k) !== i),
  );

  return (
    <Box data-field={anchor}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack spacing={1.5}>
        {rows.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            None set.
          </Typography>
        )}
        {rows.map((row, i) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Stack spacing={1} sx={{ flex: 1 }}>
              <TextField
                size="small"
                label={keyLabel}
                value={row.k}
                onChange={(e) =>
                  push(
                    rows.map((r, idx) =>
                      idx === i ? { ...r, k: e.target.value } : r,
                    ),
                  )
                }
                error={row.k === "" || duplicateKeys.has(row.k)}
                helperText={
                  row.k === ""
                    ? "Empty key — this row is dropped on save"
                    : duplicateKeys.has(row.k)
                      ? "Duplicate key — only the last row survives"
                      : 'Survey class slug, or "all"'
                }
              />
              {multiValue ? (
                <ChipListField
                  size="small"
                  label={valueLabel}
                  value={(row.v as string[]) ?? []}
                  onChange={(v) =>
                    push(
                      rows.map((r, idx) =>
                        idx === i ? { ...r, v: (v ?? []) as V } : r,
                      ),
                    )
                  }
                />
              ) : (
                <TextField
                  size="small"
                  label={valueLabel}
                  value={(row.v as string) ?? ""}
                  onChange={(e) =>
                    push(
                      rows.map((r, idx) =>
                        idx === i ? { ...r, v: e.target.value as V } : r,
                      ),
                    )
                  }
                />
              )}
            </Stack>
            <IconButton
              aria-label={`Remove ${row.k || "empty"} row`}
              size="small"
              onClick={() => push(rows.filter((_, idx) => idx !== i))}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
      <Button
        startIcon={<Add />}
        size="small"
        sx={{ mt: 1 }}
        onClick={() => {
          setSeq(seq + 1);
          setRows([
            ...rows,
            { id: seq + 1, k: "", v: (multiValue ? [] : "") as V },
          ]);
        }}
      >
        Add row
      </Button>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
